import { CSCompensation } from "@components/features/CSCompensation";
import CSCompensationDialog from "@components/features/CSCompensation/CSCompensationDialog";
import { MegaShare } from "@components/features/MegaShare";
import MegaShareFriendDialog from "@components/features/MegaShare/MegaShareFriendDialog";
import { SkinnerBox } from "@components/features/SkinnerBox";
import { Paths } from "@constants";
import useCSCompensation from "@hooks/useCSCompensation";
import useSkinnerBoxStatus from "@hooks/useSkinnerBoxStatus";
import { useMegaShareStatus } from "@hooks/useMegaShareStatus";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { getThemeAsset } from "@utils/theme";

interface PromotionFABGroupProps extends PropsWithClassName { }

const excludedRoutes = [
  Paths.PRIVATE.PROMOTION_DAILY_LOGIN,
  Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN,
];

const PromotionFABGroup = ({ className }: PromotionFABGroupProps) => {
  const location = useLocation();
  const { shouldShowCSCompensation, openCSCompensation } = useCSCompensation();
  const {
    shouldShowTakerMegaShare,
    taker: megaShareTaker,
    details: megaShareDetails,
    openFriendDialog: openMegaShareFriendDialog,
  } = useMegaShareStatus();
  const {
    shouldShowSkinnerBox,
    skinnerBoxClaimableAmount,
    skinnerBoxKey,
    handleLeaveAnimationCompleted,
  } = useSkinnerBoxStatus();

  const megaShareStatus = megaShareTaker?.status;
  const megaShareClaimAmount = megaShareDetails?.takerClaimAmount;

  const isHome = useMemo(() => matchPath(Paths.PRIVATE.HOME, location.pathname), [location.pathname]);
  const isExcludedRoute = useMemo(
    () => excludedRoutes.some((route) => matchPath(route, location.pathname)),
    [location.pathname],
  );

  const shouldShowCSCompensationFAB = isHome && shouldShowCSCompensation;

  const shouldShowMegaShareFAB =
    isHome &&
    shouldShowTakerMegaShare &&
    megaShareStatus === MegaShareState.CLAIMABLE &&
    !!megaShareClaimAmount &&
    megaShareClaimAmount > 0;

  const shouldShowSkinnerFAB = shouldShowSkinnerBox && !isExcludedRoute && !!skinnerBoxClaimableAmount;

  if (!shouldShowCSCompensationFAB && !shouldShowMegaShareFAB && !shouldShowSkinnerFAB) {
    return null;
  }

  const promotionBackground = getThemeAsset("/skinner-box/dark-background.png");

  const shouldShowBackground = shouldShowCSCompensationFAB || shouldShowSkinnerFAB || shouldShowMegaShareFAB;
  return (
    <div className={cn("fixed bottom-[56px] right-1/2 z-[5] w-full max-w-md translate-x-1/2", className)}>
      <div className="absolute bottom-0 right-0 flex w-auto max-w-md flex-col items-center">
        {shouldShowBackground && <img
          src={promotionBackground}
          alt="Promotion background"
          className="absolute bottom-0 right-0"
        />}

        {shouldShowCSCompensationFAB && <CSCompensation onClick={openCSCompensation} />}
        {shouldShowSkinnerFAB && (
          <SkinnerBox
            key={skinnerBoxKey}
            claimableAmount={skinnerBoxClaimableAmount}
            onLeaveAnimationCompleted={handleLeaveAnimationCompleted}
          />
        )}
        {shouldShowMegaShareFAB && <MegaShare onClick={openMegaShareFriendDialog} />}
      </div>

      {/* Mount dialog once; opens via custom event */}
      <CSCompensationDialog />

      <MegaShareFriendDialog />
    </div>
  );
};

export default PromotionFABGroup;
